import React, { useState, useEffect } from 'react';
import { 
  Book, Bookmark, BookmarkCheck, Users, 
  Clock, ChevronRight, ArrowLeft, Search, 
  Check, X, CreditCard, Filter, AlertTriangle, Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import useUserProfile from '../hooks/useUserProfile';

const ROUTES = {
  ADD_CREDITS: '/add-credits',
  DASHBOARD: '/dashboard',
  QUIZ: '/assessmentsquiz',
};

// Compliance types configuration
const complianceTypes = [
  { id: 'hipaa', name: 'HIPAA', description: 'Health Insurance Portability and Accountability Act' },
  { id: 'iso', name: 'ISO 27001', description: 'Information Security Management System' },
  { id: 'gdpr', name: 'GDPR', description: 'General Data Protection Regulation' },
  { id: 'sox', name: 'SOX', description: 'Sarbanes-Oxley Act' },
  { id: 'pci', name: 'PCI DSS', description: 'Payment Card Industry Data Security Standard' },
  { id: 'nist', name: 'NIST CSF', description: 'National Institute of Standards and Technology Cybersecurity Framework' },
  { id: 'ccpa', name: 'CCPA', description: 'California Consumer Privacy Act' },
  { id: 'fedramp', name: 'FedRAMP', description: 'Federal Risk and Authorization Management Program' },
  { id: 'cmmc', name: 'CMMC', description: 'Cybersecurity Maturity Model Certification' },
  { id: 'soc2', name: 'SOC 2', description: 'Service Organization Control 2' }
];

// Alert Component
const CustomAlert = ({ message, onClose }) => (
  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
    <div className="flex items-center">
      <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
      <p className="text-sm text-red-700">{message}</p>
      {onClose && (
        <button 
          onClick={onClose} 
          className="ml-auto text-red-500 hover:text-red-700 p-1"
          aria-label="Close alert"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

// Credits Exhausted Modal
const CreditsExhaustedModal = ({ onClose, onAddCredits }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Credits Exhausted</h3>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 transition-colors p-1"
          aria-label="Close modal"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex justify-center mb-6">
        <div className="p-3 bg-red-100 rounded-full">
          <CreditCard className="w-8 h-8 text-red-500" />
        </div>
      </div>

      <div className="text-center mb-8">
        <p className="text-gray-900 font-medium mb-2">
          You've used all your available credits
        </p>
        <p className="text-gray-500">
          Please add more credits to continue using our services
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        <button
          onClick={onAddCredits}
          className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors"
        >
          Add Credits
        </button>
        <button
          onClick={onClose}
          className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// MetricItem Component
const MetricItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 flex-shrink-0">
      {icon}
    </div>
    <div className="flex flex-col min-w-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 truncate">
        {value}
      </span>
    </div>
  </div>
);

// Assessment Card Component
const AssessmentCard = ({ assessment, selected, onClick, disabled }) => {
  // Get the first 3 tags only
  const displayTags = assessment.tags?.slice(0, 3) || [];
  const remainingTagsCount = (assessment.tags?.length || 0) - 3;

  return (
    <div className={`flex flex-col h-full bg-white rounded-xl border 
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'border-gray-200 hover:shadow-lg'} 
      overflow-hidden transition-all duration-200`}>
      {/* Header Section */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="mt-1 flex-shrink-0">
              <Layers className={`h-5 w-5 ${selected ? 'text-red-500' : 'text-gray-500'}`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight break-words line-clamp-2">
                {assessment.title}
              </h3>
              {assessment.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                  {assessment.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
              selected ? 'border-red-500 bg-red-500' : 'border-gray-300'
            }`}>
              {selected && <Check className="w-4 h-4 text-white" />}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 min-h-12">
          {displayTags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium"
            >
              {tag}
            </span>
          ))}
          {remainingTagsCount > 0 && (
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
              +{remainingTagsCount} more
            </span>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6 border-b border-gray-100 flex-1">
        <div className="grid grid-cols-2 gap-4">
          <MetricItem
            icon={<Book className="h-4 w-4 text-gray-500" />}
            label="Questions"
            value={assessment.questionCount}
          />
          <MetricItem
            icon={<Users className="h-4 w-4 text-gray-500" />}
            label="Evaluations"
            value={assessment.evaluationCount}
          />
          <MetricItem
            icon={<Clock className="h-4 w-4 text-gray-500" />}
            label="Levels"
            value={assessment.levelsReq}
          />
          <MetricItem
            icon={<span className="text-sm font-semibold text-gray-500">©</span>}
            label="Credits"
            value={assessment.credReq}
          />
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            {assessment.featured && (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-50 text-yellow-600">
                Featured
              </span>
            )}
            {assessment.popular && (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-600">
                Popular
              </span>
            )}
          </div>
          <span>
            Updated: {new Date(assessment.updatedAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Footer Section */}
      <div className="p-6">
        <button
          onClick={() => !disabled && onClick()}
          disabled={disabled}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg
            ${disabled 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : selected
                ? 'bg-red-50 hover:bg-red-100 text-red-600'
                : 'bg-red-50 hover:bg-red-100 text-red-600'
            } font-medium transition-colors
            focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
        >
          {selected ? 'Selected' : disabled ? 'Incompatible Compliance' : 'Select Assessment'}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Main Assessment Flow Component
const AssessmentFlow = () => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const { userProfile } = useUserProfile();
  const access_token = authData?.access_token || authData?.accessToken;

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompliance, setSelectedCompliance] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Get available credits
  const getAvailableCredits = () => parseInt(userProfile?.credits || '0', 10);

  // Check if an assessment can be selected based on compliance matching
  const canSelectAssessment = (assessment) => {
    if (selectedAssessments.length === 0) return true;
    
    const firstSelectedCompliance = selectedAssessments[0].compliance;
    return assessment.compliance.some(compliance => 
      firstSelectedCompliance.includes(compliance)
    );
  };

  // Fetch assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const filter = selectedCompliance ? { compliance: selectedCompliance.toUpperCase() } : {};
        const encodedFilter = encodeURIComponent(JSON.stringify(filter));
        const response = await fetch(
          `https://app.spiralreports.com/api/assessments/all?page=1&limit=1000&orderBy=asc&filter=${encodedFilter}`,
          {
            headers: {
              'Authorization': `Bearer ${access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (!response.ok) throw new Error('Failed to fetch assessments');
        
        const { data } = await response.json();
        setAssessments(data.results || []);
      } catch (err) {
        setError('Failed to fetch assessments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, [selectedCompliance, access_token]);

  // Handle assessment selection
  const handleAssessmentSelect = async (assessment) => {
    const availableCredits = getAvailableCredits();
    if (availableCredits === 0) {
      setShowCreditsModal(true);
      return;
    }

    setSelectedAssessments(prev => {
      const isSelected = prev.some(item => item.id === assessment.id);
      
      if (isSelected) {
        return prev.filter(item => item.id !== assessment.id);
      }
      
      if (canSelectAssessment(assessment)) {
        return [...prev, assessment];
      }
      
      setError('Can only group assessments with matching compliance types');
      return prev;
    });
  };

  // Handle add credits navigation
  const handleAddCredits = () => {
    setShowCreditsModal(false);
    navigate(ROUTES.ADD_CREDITS, { 
      state: { 
        returnPath: window.location.pathname,
        assessmentData: {
          selectedAssessments
        }
      }
    });
  };

  // Start assessment(s)
  const startAssessment = async () => {
    if (selectedAssessments.length === 0) return;
    
    try {
      setLoading(true);

      if (selectedAssessments.length === 1) {
        const response = await fetch(
          `https://app.spiralreports.com/api/assessments/${selectedAssessments[0].id}`,
          {
            headers: {
              'Authorization': `Bearer ${access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch assessment details');

        const result = await response.json();
        navigate(ROUTES.QUIZ, {
          state: {
            assessment: result.data,
            accessToken: access_token
          }
        });
      } else {
        const response = await fetch('https://app.spiralreports.com/api/package', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: 'Group Assessment',
            description: 'Multiple assessments evaluation',
            compliance: selectedCompliance.toUpperCase(),
            assessments: selectedAssessments.map(assessment => assessment.id)
          })
        });

        if (!response.ok) throw new Error('Failed to create package');

        const packageResult = await response.json();
        
        const packageEvalResponse = await fetch('https://app.spiralreports.com/api/package-eval', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: 'Group Assessment',
            description: 'Multiple assessments evaluation',
            package_id: packageResult.data._id,
            assessments: selectedAssessments.map(assessment => assessment.id)
          })
        });

        if (!packageEvalResponse.ok) throw new Error('Failed to create package evaluation');
        
        const packageEvalResult = await packageEvalResponse.json();
        const packageEvalId = packageEvalResult.data._id;

        const detailsResponse = await fetch(
          `https://app.spiralreports.com/api/package-eval/${packageEvalId}`,
          {
            headers: {
              'Authorization': `Bearer ${access_token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!detailsResponse.ok) throw new Error('Failed to fetch package evaluation details');

        const packageDetails = await detailsResponse.json();

        navigate(ROUTES.QUIZ, {
          state: {
            packageData: packageResult.data,
            packageEvalId,
            packageDetails: packageDetails.data[0],
            accessToken: access_token,
            isGroup: true
          }
        });
      }
    } catch (error) {
      setError('Failed to start assessment. Please try again.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter assessments based on search
  const filteredAssessments = assessments.filter(assessment => 
    assessment.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    assessment.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 flex justify-between items-center border-b bg-white sticky top-0 z-20 shadow-sm">
        <button
          onClick={() => navigate(ROUTES.DASHBOARD)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Exit to Dashboard</span>
        </button>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
            <CreditCard className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600">
              Credits: {getAvailableCredits()}
            </span>
          </div>

          {selectedAssessments.length > 0 && (
            <button
              onClick={startAssessment}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                loading 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              <span>
                {loading 
                  ? 'Starting...' 
                  : selectedAssessments.length > 1 
                  ? 'Start Group Assessment' 
                  : 'Start Assessment'
                }
              </span>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Search and Filters */}
          <div className="space-y-4 sticky top-20 z-10 bg-gray-50 pt-2">
            {/* Search Bar */}
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search assessments..."
                className="block w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-500 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              />
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-900">Compliance Type</span>
                </div>
                <button 
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-red-500 text-sm hover:text-red-600 font-medium"
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
              </div>

              {showFilters && (
                <div className="px-4 pb-4">
                  <select
                    value={selectedCompliance}
                    onChange={(e) => setSelectedCompliance(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                  >
                    <option value="">All Compliance Types</option>
                    {complianceTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && <CustomAlert message={error} onClose={() => setError(null)} />}

          {/* Assessment Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
              </div>
            ) : filteredAssessments.length > 0 ? (
              filteredAssessments.map(assessment => (
                <AssessmentCard
                  key={assessment.id}
                  assessment={assessment}
                  selected={selectedAssessments.some(item => item.id === assessment.id)}
                  onClick={() => handleAssessmentSelect(assessment)}
                  disabled={selectedAssessments.length > 0 && 
                           !selectedAssessments.some(item => item.id === assessment.id) && 
                           !canSelectAssessment(assessment)}
                />
              ))
            ) : (
              <div className="col-span-full flex flex-col items-center justify-center py-12 px-4">
                <AlertTriangle className="w-12 h-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Assessments Found</h3>
                <p className="text-sm text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Credits Modal */}
      {showCreditsModal && (
        <CreditsExhaustedModal
          onClose={() => setShowCreditsModal(false)}
          onAddCredits={handleAddCredits}
        />
      )}
    </div>
  );
};

export default AssessmentFlow;